import { useState } from "react";
import { Flag, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, apiError } from "@/lib/api";
import { cn } from "@/lib/utils";

const REASONS = [
  ["copyright", "Copyright"], ["sexual", "Nudity or sexual content"], ["harassment", "Harassment"],
  ["spam", "Spam or scam"], ["fake", "Fake profile"], ["other", "Other"],
];
const LABEL = { photo: "photo", video: "video", message: "message" };

export const ReportButton = ({ kind, target, className, compact = false, testId }) => {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!reason) return toast.error("Please choose a reason");
    if (reason === "other" && note.trim().length < 3) return toast.error("Please add a short note");
    setBusy(true);
    try {
      await api.post("/reports", { kind, target, reason, note });
      toast.success("Thanks. Our moderators will review this.");
      setOpen(false); setReason(""); setNote("");
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <>
      <button type="button" onClick={(e) => { e.stopPropagation(); setOpen(true); }} aria-label={`Report this ${LABEL[kind]}`}
        className={cn("inline-flex items-center gap-1.5 text-xs text-muted-foreground transition-colors hover:text-destructive", className)} data-testid={testId || `report-${kind}-button`}>
        <Flag className="h-3.5 w-3.5" />{!compact && "Report"}
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="z-[120] max-w-md border-border bg-card" data-testid="report-dialog" onClick={(e) => e.stopPropagation()}>
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Report this {LABEL[kind]}</DialogTitle>
            <DialogDescription>Reports are private. The member won't see who reported them.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-2" role="radiogroup" aria-label="Reason">
            {REASONS.map(([id, label]) => (
              <button key={id} type="button" role="radio" aria-checked={reason === id} onClick={() => setReason(id)} className="chip justify-start" data-active={reason === id} data-testid={`report-reason-${id}`}>{label}</button>
            ))}
          </div>
          <label htmlFor="report-note" className="field-label mt-2">Note {reason === "other" ? "" : <span className="font-normal text-muted-foreground">(optional)</span>}</label>
          <textarea id="report-note" className="field min-h-[90px]" maxLength={1000} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Anything that helps our moderators" data-testid="report-note-input" />
          <div className="mt-2 flex justify-end gap-2">
            <button type="button" className="btn-outline h-11 px-4 text-sm" onClick={() => setOpen(false)} data-testid="report-cancel">Cancel</button>
            <button type="button" className="btn-primary h-11 px-5 text-sm" onClick={submit} disabled={busy} data-testid="report-submit">{busy && <Loader2 className="h-4 w-4 animate-spin" />} Send report</button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
